import React, { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useBreakpoint } from 'hooks';
import { TOP_BAR_HEIGHT } from '_constants/LAYOUT';
import { size as BREAKPOINTS } from '_constants/DEVICES';
// Direct module path, not the utils/custom barrel (which pulls the store in).
import { registerPrintableTable } from 'utils/custom/tablePdf/printMode';
import * as Styled from './styles';

// The off-screen desktop table a PDF export reads on a narrow screen is inert: no focus, no clicks,
// hidden from screen readers. React 17 doesn't know the `inert` attribute, so it is set directly.
const makeInert = (el) => el?.setAttribute('inert', '');

/** The element a table scrolls within — a modal or drawer body — or null when it's the page itself.
 *  Only an ancestor that actually scrolls counts: `overflow-x: hidden` alone computes overflow-y to
 *  `auto` without making anything scroll. */
const findScrollParent = (el) => {
  let node = el?.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return null;
};

// Below this width the mobile chrome scrolls away with the page (see _constants/LAYOUT).
const isMobileChrome = () => Boolean(window.matchMedia?.(`(max-width: ${BREAKPOINTS.tableView})`).matches);

/**
 * Reusable, presentational table shell shared across list pages (the "new design" look:
 * rounded bordered card, sticky uppercase headers, hover rows, toolbar + footer slots).
 *
 * Composition-first — every part is opt-in so tables can differ:
 *  - `columns`  : [{ key, label|header, width, center, headerControl?, render(row) }]
 *  - `toolbar`  : { left, lead, actions, meta, metaRight, bottom, pinEnd, stickyTop } — any subset of slots
 *  - `mobile`   : node rendered instead of the table on compact breakpoints (omit -> table is shown everywhere)
 *  - `rowProps` : (row) => props spread onto the <Tr> (e.g. `{ revisit: true }` for the row highlight)
 *  - `col.cellProps` : (row) => props spread onto that column's <Td> (`rowSpan`, `colSpan`, `className`),
 *                 or null to leave the cell out because a span from another row or column covers it
 *  - `view`/`mapSlot` : swap the body for a map (or any alternate view)
 *  - `stickyHeader` : with a sticky toolbar (`toolbar.stickyTop`), pin the column headers right under it
 *
 * Print / Save to PDF (utils/custom/tablePdf) reads the table straight from this markup: the wrapper is
 * `data-pdf-table`, each header carries its column's `label` as `data-pdf-label` (a sortable header is a
 * button, which the reader leaves out), and columns without a label — selection, row actions — are
 * `data-pdf-skip` on every cell.
 *
 * Sorting, selection, pagination etc. are NOT baked in — pass them through the slots /
 * column header nodes so each consumer keeps full control.
 */
const DataTable = ({
  columns,
  data,
  rowKey,
  rowProps,
  toolbar,
  footer,
  emptyText,
  view,
  mapSlot,
  mobile,
  scrollable,
  minWidth,
  sticky,
  stickyHeader,
}) => {
  const breakpoint = useBreakpoint();

  // Print / Save to PDF (utils/custom/tablePdf) reads the desktop table. While an export asks for it,
  // a narrow screen renders that table too — off-screen, at desktop width — next to its cards, so a
  // phone prints what a big screen does. `pdfRequest.done` answers once it is laid out.
  const [pdfRequest, setPdfRequest] = useState(null);
  useEffect(() => registerPrintableTable((active, done) => setPdfRequest(active ? { done } : null)), []);
  useEffect(() => {
    if (!pdfRequest) return undefined;
    const frame = requestAnimationFrame(() => pdfRequest.done());
    return () => cancelAnimationFrame(frame);
  }, [pdfRequest]);
  const isCompact = breakpoint !== 'desktop';

  // `pinEnd`: pin `actions` to the top-right corner of the row and gather everything else (`left` and
  // `lead`) into one group that wraps within the width left over. For a toolbar whose actions are a
  // fixed reference point — the media galleries' record/file counts and pager — this is the only
  // layout that keeps them in the corner: in a plain wrapping row they are just another item and drop
  // below the controls as soon as the line fills. See Styled.ToolbarPinRow.
  const { left, lead, actions, meta, metaRight, bottom, pinEnd, stickyTop, stickyTopCompact } = toolbar || {};
  const hasTopRow = left || lead || actions;
  const hasMetaRow = meta || metaRight;
  const hasToolbar = hasTopRow || hasMetaRow || bottom;

  // Every table follows the scroll (`sticky`, on by default): its toolbar pins under the page's top
  // bar, and the column headers pin under the toolbar. Inside a modal or drawer that scrolls on its
  // own, both pin to the top of that box instead. A page can still set its own offsets
  // (toolbar.stickyTop / stickyTopCompact), or opt out with `sticky={false}`.
  const wrapperRef = useRef(null);
  const tableRef = useRef(null);
  const [scroller, setScroller] = useState(null);
  // Re-checked as rows arrive: a modal body only starts scrolling once its content is there.
  useLayoutEffect(() => {
    if (sticky) setScroller(findScrollParent(wrapperRef.current));
  }, [sticky, data.length]);

  const pinTop = stickyTop ?? (sticky ? (scroller ? 0 : TOP_BAR_HEIGHT) : undefined);
  const pinTopCompact = stickyTopCompact ?? (sticky ? 0 : undefined);

  // The headers pin where the toolbar ends. That edge moves — the toolbar wraps onto more lines as
  // the window narrows, and its own offset changes with the breakpoint — so it is measured rather
  // than computed. Without a toolbar they pin where the toolbar would have.
  const toolbarRef = useRef(null);
  const [headTop, setHeadTop] = useState(null);
  const pinHeader = Boolean(stickyHeader && pinTop != null && view !== 'map');

  useLayoutEffect(() => {
    const el = toolbarRef.current;
    if (!pinHeader) {
      setHeadTop(null);
      return undefined;
    }
    const measure = () =>
      setHeadTop(
        el
          ? (parseFloat(window.getComputedStyle(el).top) - 2 || 0) + el.offsetHeight
          : isMobileChrome()
          ? pinTopCompact
          : pinTop,
      );
    // Re-measure on the next frame, not inside the observer's delivery: a layout change made there
    // reports back in the same delivery — the "ResizeObserver loop" error.
    let frame = 0;
    const scheduleMeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    measure();
    const observer = el && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleMeasure) : null;
    observer?.observe(el);
    window.addEventListener('resize', scheduleMeasure);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [pinHeader, hasToolbar, pinTop, pinTopCompact]);

  // A `scrollable` table sits in a horizontal-scroll box, and a sticky cell can't pin past such a box
  // to the page. So there a copy of the header row is drawn above the box, in a container of its own
  // that pins with plain CSS — exactly like a regular table, no lag — and follows the box sideways
  // (scrollLeft synced from the box). The table keeps its real header row at zero height: it still
  // fixes the column widths and names the columns for screen readers, but isn't seen.
  //
  // The copy is what sighted users see and click (sorting); it is hidden from screen readers. Neither
  // row is in the Tab order — one is invisible, the other aria-hidden — so keyboard users sort with
  // the toolbar's "Sort:" menu, which ListTable adds wherever columns sort.
  // From the first render (the toolbar's height lands a frame later), so the header never swaps in.
  const cloneHead = pinHeader && scrollable;
  const headScrollRef = useRef(null);
  const bodyScrollRef = useRef(null);

  const syncHeadScroll = () => {
    if (headScrollRef.current && bodyScrollRef.current) {
      headScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    }
  };

  useEffect(() => {
    if (!cloneHead) return;
    syncHeadScroll();
    [headScrollRef.current, tableRef.current?.tHead].forEach((root) =>
      root?.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((el) => {
        el.setAttribute('tabindex', '-1');
      }),
    );
  });

  // Which of the card's rounded corners the table itself has to reproduce: only the edges where
  // nothing else sits between the table and the frame. See Table's $roundTop / $roundBottom.
  const roundTop = !hasToolbar;
  const roundBottom = !footer && data.length > 0;

  const headerRow = (
    <tr>
      {columns.map((col) => (
        <Styled.Th
          key={col.key}
          width={col.width}
          center={col.center}
          aria-sort={col.ariaSort}
          data-pdf-label={typeof col.label === 'string' ? col.label : undefined}
          data-pdf-skip={col.label ? undefined : ''}
        >
          {col.header || (
            <Styled.HeaderInner>
              <span>{col.label}</span>
              {col.headerControl}
            </Styled.HeaderInner>
          )}
        </Styled.Th>
      ))}
    </tr>
  );

  const table = (
    <Styled.Table
      ref={tableRef}
      minWidth={minWidth}
      $roundTop={roundTop && !cloneHead}
      $roundBottom={roundBottom}
      $headTop={cloneHead ? null : headTop}
      $hiddenHead={cloneHead}
    >
      <thead>{headerRow}</thead>
      <tbody>
        {/* rowKey receives the index as a second argument: some payloads repeat an id across rows
            (the revisits list is one row per violation but keyed by RETAILER, so a retailer needing
            two revisits appears twice), and a consumer needs something to break the tie with. */}
        {data.map((row, index) => (
          <Styled.Tr key={rowKey(row, index)} {...(rowProps ? rowProps(row) : {})}>
            {columns.map((col) => {
              const cellProps = col.cellProps ? col.cellProps(row) : {};
              if (cellProps === null) return null;
              return (
                <Styled.Td key={col.key} center={col.center} data-pdf-skip={col.label ? undefined : ''} {...cellProps}>
                  {col.render(row)}
                </Styled.Td>
              );
            })}
          </Styled.Tr>
        ))}
      </tbody>
    </Styled.Table>
  );

  return (
    <Styled.Wrapper ref={wrapperRef} data-pdf-table="">
      {hasToolbar && (
        <Styled.Toolbar ref={toolbarRef} $stickyTop={pinTop} $stickyTopCompact={pinTopCompact}>
          {hasTopRow &&
            (pinEnd ? (
              <Styled.ToolbarPinRow>
                <Styled.ToolbarControls>
                  {left}
                  {lead}
                </Styled.ToolbarControls>
                <Styled.ToolbarPinned>{actions}</Styled.ToolbarPinned>
              </Styled.ToolbarPinRow>
            ) : (
              <Styled.ToolbarRow>
                <Styled.ToolbarLeft>{left}</Styled.ToolbarLeft>
                <Styled.ToolbarRight>
                  {lead}
                  {actions}
                </Styled.ToolbarRight>
              </Styled.ToolbarRow>
            ))}
          {hasMetaRow && (
            <Styled.ToolbarRow>
              <Styled.MetaLeft>{meta}</Styled.MetaLeft>
              <Styled.ToolbarRight>{metaRight}</Styled.ToolbarRight>
            </Styled.ToolbarRow>
          )}
          {bottom && <Styled.ToolbarRow>{bottom}</Styled.ToolbarRow>}
        </Styled.Toolbar>
      )}

      {view === 'map' && mapSlot ? (
        mapSlot
      ) : isCompact && mobile ? (
        <>
          {mobile}
          {pdfRequest && (
            <Styled.PdfOffscreen ref={makeInert} aria-hidden="true">
              {table}
            </Styled.PdfOffscreen>
          )}
          {footer && <Styled.Footer>{footer}</Styled.Footer>}
        </>
      ) : (
        <>
          {cloneHead && (
            <Styled.StickyHead
              ref={headScrollRef}
              $top={headTop ?? pinTop}
              aria-hidden="true"
              // Print / Save to PDF reads the table itself, not this copy.
              data-pdf-ignore=""
            >
              <Styled.Table minWidth={minWidth} $roundTop={roundTop}>
                <thead>{headerRow}</thead>
              </Styled.Table>
            </Styled.StickyHead>
          )}
          {scrollable ? (
            <Styled.TableScroll ref={bodyScrollRef} onScroll={cloneHead ? syncHeadScroll : undefined}>
              {table}
            </Styled.TableScroll>
          ) : (
            table
          )}
          {data.length === 0 && <Styled.EmptyState>{emptyText}</Styled.EmptyState>}
          {footer && <Styled.Footer>{footer}</Styled.Footer>}
        </>
      )}
    </Styled.Wrapper>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node,
      header: PropTypes.node,
      headerControl: PropTypes.node,
      width: PropTypes.string,
      center: PropTypes.bool,
      ariaSort: PropTypes.oneOf(['ascending', 'descending', 'none']),
      render: PropTypes.func.isRequired,
      cellProps: PropTypes.func,
    }),
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object),
  rowKey: PropTypes.func.isRequired,
  rowProps: PropTypes.func,
  toolbar: PropTypes.shape({
    left: PropTypes.node,
    lead: PropTypes.node,
    actions: PropTypes.node,
    meta: PropTypes.node,
    metaRight: PropTypes.node,
    bottom: PropTypes.node,
    pinEnd: PropTypes.bool,
    // px below the viewport top to pin the toolbar at (the page's top bar); omit to leave it static.
    stickyTop: PropTypes.number,
    // Same, for the mobile chrome's taller bar; defaults to stickyTop.
    stickyTopCompact: PropTypes.number,
  }),
  footer: PropTypes.node,
  emptyText: PropTypes.node,
  view: PropTypes.oneOf(['list', 'map']),
  mapSlot: PropTypes.node,
  mobile: PropTypes.node,
  // Opt-in horizontal scroll + table min-width so columns don't collapse on narrow desktops.
  scrollable: PropTypes.bool,
  minWidth: PropTypes.string,
  // Toolbar and headers follow the scroll (see above); false leaves the table static.
  sticky: PropTypes.bool,
  // Pin the column headers too (under the toolbar). On with `sticky`.
  stickyHeader: PropTypes.bool,
};

DataTable.defaultProps = {
  data: [],
  rowProps: undefined,
  toolbar: null,
  footer: null,
  emptyText: 'No records found.',
  view: 'list',
  mapSlot: null,
  mobile: null,
  scrollable: false,
  minWidth: undefined,
  sticky: true,
  stickyHeader: true,
};

export default memo(DataTable);
